import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Copy, HelpCircle, Lock, Mail, Plus, RefreshCw, Send } from 'lucide-react';
import { Button } from '@cloudsam/ui';
import { useDraftSaver } from '../../contexts/IdleContext';
import { CHANNEL_BINDING_DEFINITIONS, bindingDefinitionForMode } from './channelBindingDefinitions';
import { persistedVersionIsCurrent } from './draftPersistence';
import {
    createManagedBinding, getManagedChannel, saveAccountDraft,
    getInstructionDelivery, saveBindingDraft, saveInstructionProgress, sendInstructions, testBindingDraft,
    transitionManagedBinding, stopManagedBinding,
} from '../../utils/bindingsApi';

const parse = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try { return JSON.parse(value); } catch (_) { return {}; }
};

const availableConfig = (active, draft) => ({ ...parse(active), ...parse(draft) });
const fieldDefaults = (fields = []) => Object.fromEntries(fields
    .filter((item) => item.defaultValue !== undefined)
    .map((item) => [item.name, item.defaultValue]));
const withFieldDefaults = (fields, values) => ({ ...fieldDefaults(fields), ...values });
const isWorkspaceSender = (item) => ({ ...parse(item?.binding?.platform_identifiers), ...availableConfig(item?.settings?.active_config, item?.settings?.draft_config) }).connection_mode === 'workspace_user';
const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const messengerDisplayValues = (pageId) => {
    const base = String(process.env.REACT_APP_CHANNEL_GATE_URL || 'https://survey.samsurveyor.com').replace(/\/+$/, '');
    const route = String(pageId || '').trim();
    return {
        callback_url: route ? `${base}/webhooks/messenger?binding_route_key=${encodeURIComponent(route)}` : `${base}/webhooks/messenger`,
        subscribed_fields: 'messages,messaging_postbacks',
    };
};

// The Google Chat endpoint carries the binding route in the URL because newer
// Google configurations sign events with the endpoint URL, not the project
// number, so the URL itself must say which connection owns the event.
const googleChatDisplayValues = (projectNumber) => {
    const base = String(process.env.REACT_APP_CHANNEL_GATE_URL || 'https://survey.samsurveyor.com').replace(/\/+$/, '');
    const route = String(projectNumber || '').trim();
    return { connection_endpoint: route ? `${base}/webhooks/googlechat?binding_route_key=${encodeURIComponent(route)}` : `${base}/webhooks/googlechat` };
};

async function waitForInstructionDelivery(bindingId, deliveryId) {
    for (let attempt = 0; attempt < 20; attempt += 1) {
        await wait(1000);
        const delivery = await getInstructionDelivery(bindingId, deliveryId);
        if (delivery.status === 'sent' || delivery.status === 'failed') return delivery;
    }
    return null;
}

function Field({ scope, item, value, disabled, configured, onChange }) {
    const id = `binding-field-${scope}-${item.name}`;
    const common = { id, disabled: !item.copy && (disabled || item.readOnly), readOnly: Boolean(item.readOnly), value: value ?? '', onChange: (event) => onChange(item, event.target.value) };
    let control;
    if (item.type === 'select') {
        const knownValue = !common.value || (item.options || []).some(([key]) => key === common.value);
        control = <select {...common}>{!knownValue && <option value={common.value} disabled>Unsupported saved value ({common.value})</option>}{(item.options || []).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>;
    } else if (item.type === 'textarea') {
        control = <textarea {...common} rows={3} />;
    } else if (item.type === 'checkbox') {
        control = <input id={id} type="checkbox" disabled={disabled || item.readOnly} checked={Boolean(value)} onChange={(event) => onChange(item, event.target.checked)} />;
    } else if (item.type === 'file') {
        control = <input id={id} type="file" disabled={disabled || item.readOnly} onChange={async (event) => onChange(item, event.target.files?.[0] ? await event.target.files[0].text() : '')} />;
    } else {
        control = <div className={item.copy ? 'binding-copy-control' : undefined}><input {...common} type={item.secret ? 'password' : item.type} placeholder={item.secret && configured ? 'Configured — enter only to replace' : ''} />{item.copy && <button type="button" disabled={!value} aria-label={`Copy ${item.label}`} title={`Copy ${item.label}`} onClick={() => navigator.clipboard?.writeText(String(value ?? ''))}><Copy size={14} /></button>}</div>;
    }
    return (
        <label className={`binding-field${item.type === 'checkbox' ? ' binding-field--check' : ''}`} htmlFor={id}>
            <span>{item.label}{item.required ? ' *' : ''}</span>
            {control}
            {item.hint && <small><Lock size={12} /> {item.hint}</small>}
        </label>
    );
}

// A context dropdown: one collapsible group so the user can hide the fields
// they are not working on. Open state is local; the browser toggle stays in
// sync so a re-render cannot snap a section back open.
function Context({ title, defaultOpen = false, sub = false, children }) {
    const [open, setOpen] = useState(Boolean(defaultOpen));
    return (
        <details className={`binding-context${sub ? ' binding-context--sub' : ''}`} open={open} onToggle={(event) => setOpen(event.target.open)}>
            <summary>{title}</summary>
            <div className="binding-context__body">{children}</div>
        </details>
    );
}

function QuietRow({ label, value }) {
    return <div className="binding-readonly-row"><span>{label}</span><code>{value || '—'}</code></div>;
}

function GoogleFieldGrid({ names, definition, values, secrets, disabled, onChange }) {
    const fields = names.map((name) => definition.bindingFields.find((item) => item.name === name)).filter(Boolean);
    const editable = fields.filter((item) => !(item.readOnly && !item.copy));
    const quiet = fields.filter((item) => item.readOnly && !item.copy);
    return <>
        {editable.length > 0 && <div className="binding-field-grid">
            {editable.map((item) => <Field key={item.name} scope="binding" item={item} value={values[item.name]} disabled={disabled} configured={secrets.has(`binding.${item.name}`)} onChange={onChange} />)}
        </div>}
        {quiet.map((item) => <QuietRow key={item.name} label={item.label} value={values[item.name]} />)}
    </>;
}

function FieldSet({ scope, title, fields, values, secrets, disabled, onChange }) {
    if (!fields?.length) return null;
    return (
        <fieldset className="binding-fieldset" aria-disabled={disabled || undefined}>
            <legend>{title}</legend>
            <div className="binding-field-grid">
                {fields.map((item) => <Field key={item.name} scope={scope} item={item} value={values[item.name]} disabled={disabled} configured={secrets.has(`${scope}.${item.name}`)} onChange={onChange} />)}
            </div>
        </fieldset>
    );
}

function InstructionPanel({ definition, bindingId, progress, onProgress, onSend, sending }) {
    const [expanded, setExpanded] = useState(false);
    const fieldInstructions = new Map((definition.fieldInstructions || []).map((row) => [row[0], row]));
    const contentId = 'binding-setup-instructions';
    return (
        <section className="binding-instructions" aria-label="Setup instructions">
            <div className="binding-section-heading">
                <div>
                    <h3>Setup instructions</h3>
                    <p>Progress is shared with everyone in this organization. Email requests are queued; delivery is tracked separately.</p>
                </div>
                <div className="binding-instructions__actions">
                    <Button variant="secondary" disabled={!bindingId || sending} onClick={onSend}><Mail size={15} /> Send these instructions</Button>
                    <button
                        type="button"
                        className="binding-instructions__toggle"
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} setup instructions`}
                        aria-expanded={expanded}
                        aria-controls={expanded ? contentId : undefined}
                        title={`${expanded ? 'Collapse' : 'Expand'} setup instructions`}
                        onClick={() => setExpanded((current) => !current)}
                    >
                        <ChevronDown size={18} aria-hidden="true" />
                    </button>
                </div>
            </div>
            {expanded && <div id={contentId} className="binding-instructions__content">
                <h4>What you will need before you start</h4>
                <ul>{definition.prerequisites.map((item) => <li key={item}>{item}</li>)}</ul>
                <h4>Numbered steps</h4>
                <ol className="binding-step-list">{definition.steps.map((step, index) => {
                    const key = `step_${index + 1}`;
                    const checkbox = <input aria-label={`Mark step ${index + 1} complete`} type="checkbox" checked={Boolean(progress[key])} disabled={!bindingId} onClick={(event) => event.stopPropagation()} onChange={(event) => onProgress(key, event.target.checked)} />;
                    const actions = definition.stepActions?.[index] || [];
                    const rows = (definition.stepFields?.[index] || []).map((name) => fieldInstructions.get(name)).filter(Boolean);
                    return <li key={key}><details><summary>{checkbox}<span><b>{index + 1}.</b> {step}</span><ChevronDown size={15} /></summary><div className="binding-step-detail">
                        <h5>What to do</h5>
                        <ol>{actions.map((action) => <li key={action}>{action}</li>)}</ol>
                        {rows.length > 0 && <><h5>Fields used in this step</h5><FieldInstructionTable rows={rows} /></>}
                    </div></details></li>;
                })}</ol>
                <h4>Where this can go wrong</h4>
                <ul>{definition.failures.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>}
        </section>
    );
}

function FieldInstructionTable({ rows }) {
    return <div className="binding-field-reference"><table>
        <thead><tr><th>Field name</th><th>What it means</th><th>How to enter data into it</th></tr></thead>
        <tbody>{rows.map(([name, meaning, entry]) => <tr key={name}><th scope="row">{name}</th><td>{meaning}</td><td>{entry}</td></tr>)}</tbody>
    </table></div>;
}

function MemberTable({ definition, members }) {
    return (
        <section className="binding-members">
            <div className="binding-section-heading"><div><h3>{definition.members.title}</h3><p>{members.length ? `${members.length} recorded` : 'No provider members have been recorded yet.'}</p></div></div>
            <div className="binding-member-table" role="table">
                <div role="row" className="binding-member-row binding-member-row--head">{definition.members.columns.map((column) => <span role="columnheader" key={column}>{column}</span>)}</div>
                {members.length ? members.map((member) => <div role="row" className="binding-member-row" key={member.member_id}>{[member.display_label, member.external_id, member.state, member.last_confirmed_at || '—'].map((cell, index) => <span role="cell" key={index}>{cell || '—'}</span>)}</div>) : <div className="binding-member-empty">—</div>}
            </div>
        </section>
    );
}

const ChannelBindingPanel = forwardRef(function ChannelBindingPanel({
    channelId,
    canAdmin,
    editing,
    onCancel,
    onEditingChange,
    onControlStateChange,
}, controlRef) {
    const definition = CHANNEL_BINDING_DEFINITIONS[channelId];
    const unavailable = Boolean(definition?.disabled);
    const [state, setState] = useState(null);
    const [selectedId, setSelectedId] = useState('');
    const [accountDraft, setAccountDraft] = useState({});
    const [bindingDraft, setBindingDraft] = useState({});
    const isGoogleChat = channelId === 'googlechat';
    const isSender = isGoogleChat && bindingDraft.connection_mode === 'workspace_user';
    const visibleDefinition = bindingDefinitionForMode(channelId, bindingDraft);
    const [accountSecrets, setAccountSecrets] = useState({});
    const [bindingSecrets, setBindingSecrets] = useState({});
    const [locallyStoredSecrets, setLocallyStoredSecrets] = useState(() => new Set());
    const [readiness, setReadiness] = useState(null);
    const [, setDirty] = useState({ account: false, binding: false });
    const [error, setError] = useState('');
    const [busy, setBusy] = useState('');
    const [instructionNotice, setInstructionNotice] = useState('');
    const [actionNotice, setActionNotice] = useState('');
	const draftsRef = useRef({ account: {}, binding: {} });
	const secretsRef = useRef({ account: {}, binding: {} });
	const dirtyRef = useRef({ account: false, binding: false });
	const versionsRef = useRef({ account: 0, binding: 0 });
	const activePersistRef = useRef(null);

    const load = useCallback(async () => {
        setError('');
        try {
            const next = await getManagedChannel(channelId);
            setState(next);
            // An existing connection may predate the management draft. Show
            // active DBAM values as the editable baseline, with any newer
            // draft values taking precedence.
            const nextAccount = availableConfig(next.account?.active_config, next.account?.draft_config);
			setAccountDraft(nextAccount);
			draftsRef.current.account = nextAccount;
            setSelectedId((current) => {
                if (current && next.bindings.some((item) => item.binding.binding_id === current)) return current;
                const preferred = channelId === 'googlechat' ? next.bindings.find((item) => isWorkspaceSender(item) && item.binding.state !== 'disabled') || next.bindings.find(isWorkspaceSender) : null;
                return preferred?.binding?.binding_id || next.bindings[0]?.binding?.binding_id || '';
            });
        } catch (loadError) {
            // A deployed Channels context is only reachable through a signed-in
            // Console. Localhost deliberately has no Console cookie; that is a
            // review condition, not useful panel content.
            if (loadError.code !== 'NOT_SIGNED_IN') setError(loadError.message);
        }
    }, [channelId]);

    useEffect(() => { load(); }, [load]);
    const selected = useMemo(() => state?.bindings?.find((item) => item.binding.binding_id === selectedId) || null, [state, selectedId]);
    useEffect(() => {
		const runtimeValues = parse(selected?.binding?.platform_identifiers);
		const nextBinding = withFieldDefaults(definition.bindingFields, {
			...runtimeValues,
			...(selected?.binding?.display_label ? { display_label: selected.binding.display_label } : {}),
			...availableConfig(selected?.settings?.active_config, selected?.settings?.draft_config),
		});
		setBindingDraft(nextBinding);
		draftsRef.current.binding = nextBinding;
		const accountFieldNames = new Set((definition.accountFields || []).filter((item) => !item.secret && !item.readOnly).map((item) => item.name));
		const accountFallback = Object.fromEntries(Object.entries(runtimeValues).filter(([name]) => accountFieldNames.has(name)));
		if (Object.keys(accountFallback).length) {
			setAccountDraft((current) => {
				const nextAccount = { ...accountFallback, ...current };
				draftsRef.current.account = nextAccount;
				return nextAccount;
			});
		}
		setReadiness(selected?.settings?.readiness_state && selected.settings.readiness_state !== 'untested'
			? parse(selected?.settings?.readiness_result)
			: null);
		setBindingSecrets({}); secretsRef.current.binding = {};
		setLocallyStoredSecrets((current) => new Set([...current].filter((name) => !name.startsWith('binding.'))));
		setInstructionNotice('');
		setDirty((current) => ({ ...current, binding: false }));
		dirtyRef.current.binding = false;
    }, [definition.accountFields, definition.bindingFields, selectedId, selected]);
    useEffect(() => { setActionNotice(''); }, [selectedId]);

    const secretPresence = new Set([
		...(selected?.secrets || []).filter((item) => item.draft_configured || item.active_configured).map((item) => item.secret_name),
		...locallyStoredSecrets,
	]);
    const edit = (scope, item, value) => {
        setReadiness(null);
        setDirty((current) => ({ ...current, [scope]: true }));
		dirtyRef.current[scope] = true;
		versionsRef.current[scope] += 1;
        if (item.secret) {
			const next = { ...secretsRef.current[scope], [item.name]: value };
			secretsRef.current[scope] = next;
			(scope === 'account' ? setAccountSecrets : setBindingSecrets)(next);
        } else {
			const next = { ...draftsRef.current[scope], [item.name]: value };
			draftsRef.current[scope] = next;
			(scope === 'account' ? setAccountDraft : setBindingDraft)(next);
        }
    };

	const persist = useCallback(async (requestedScopes) => {
		if (activePersistRef.current) await activePersistRef.current;
		const scopes = requestedScopes || { ...dirtyRef.current };
        if (scopes.binding && !selectedId) throw new Error(`Add a ${definition.bindingName} before editing its settings.`);
		const snapshot = {
			account: { config: draftsRef.current.account, secrets: secretsRef.current.account, version: versionsRef.current.account },
			binding: { config: draftsRef.current.binding, secrets: secretsRef.current.binding, version: versionsRef.current.binding },
		};
		const operation = (async () => {
			if (scopes.account) await saveAccountDraft(channelId, snapshot.account.config, snapshot.account.secrets);
			if (scopes.binding) await saveBindingDraft(selectedId, snapshot.binding.config, snapshot.binding.secrets);
		})();
		activePersistRef.current = operation;
		try {
			await operation;
			for (const scope of ['account', 'binding']) {
				if (!scopes[scope] || !persistedVersionIsCurrent(snapshot[scope].version, versionsRef.current[scope])) continue;
				const storedNames = Object.entries(snapshot[scope].secrets)
					.filter(([, value]) => String(value || '').trim())
					.map(([name]) => `${scope}.${name}`);
				if (storedNames.length) {
					setLocallyStoredSecrets((current) => new Set([...current, ...storedNames]));
				}
				dirtyRef.current[scope] = false;
				secretsRef.current[scope] = {};
				(scope === 'account' ? setAccountSecrets : setBindingSecrets)({});
				setDirty((current) => ({ ...current, [scope]: false }));
			}
		} finally {
			if (activePersistRef.current === operation) activePersistRef.current = null;
		}
	}, [channelId, definition.bindingName, selectedId]);

	const flushDraft = useCallback(async () => {
		try {
			if (activePersistRef.current) await activePersistRef.current;
			const pending = { ...dirtyRef.current };
			if (!pending.account && !pending.binding) return;
			await persist(pending);
			if (dirtyRef.current.account || dirtyRef.current.binding) await persist({ ...dirtyRef.current });
		} catch (draftError) {
			setError(draftError.message);
			throw draftError;
		}
	}, [persist]);

    const runTest = async () => {
        setBusy('test'); setError('');
		try {
			await flushDraft();
			const response = await testBindingDraft(selectedId);
			setReadiness(response.result);
			await load();
			setActionNotice(response.result?.state === 'passed'
				? 'Test passed. This connection is active and ready to send surveys.'
				: 'Test failed. Correct the problems below and run Test again.');
			if (response.result?.state === 'passed') onEditingChange?.(false);
		}
        catch (testError) { setError(testError.message); setReadiness(null); }
        finally { setBusy(''); }
    };
    const save = async () => {
        setBusy('save'); setError('');
        try {
			await flushDraft();
			setActionNotice('Draft saved. Run Test when you are ready to verify and activate this connection.');
			await load();
		}
        catch (saveError) { setError(saveError.message); }
        finally { setBusy(''); }
    };
    const add = async (googleMode = 'workspace_user') => {
        if (!canAdmin || unavailable || busy) return;
        setBusy('add'); setError('');
		try {
			await flushDraft();
			const binding = await createManagedBinding(channelId, `New ${definition.bindingName}`, definition.provider);
			const defaults = { ...fieldDefaults(definition.bindingFields), ...(isGoogleChat ? { connection_mode: googleMode } : {}) };
			if (Object.keys(defaults).length) {
				// Creating a sender binding does not create its management settings.
				// GetState initializes that row before the first defaults PUT.
				await getManagedChannel(channelId);
				await saveBindingDraft(binding.binding_id, defaults);
			}
			await load();
			setSelectedId(binding.binding_id);
            onEditingChange?.(true);
		}
        catch (addError) { setError(addError.message); }
        finally { setBusy(''); }
    };
	const selectBinding = async (bindingID) => {
		if (bindingID === selectedId || busy) return;
		setBusy('draft'); setError('');
		try {
            await flushDraft();
            setSelectedId(bindingID);
            if (state?.bindings?.find((item) => item.binding.binding_id === bindingID)?.binding.state === 'disabled') onEditingChange?.(false);
        }
		catch (draftError) { setError(draftError.message); }
		finally { setBusy(''); }
	};
    const updateProgress = async (key, checked) => {
        const next = { ...parse(selected?.settings?.instruction_progress), [key]: checked };
        setState((current) => ({ ...current, bindings: current.bindings.map((item) => item.binding.binding_id === selectedId ? { ...item, settings: { ...item.settings, instruction_progress: next } } : item) }));
        try { await saveInstructionProgress(selectedId, next); } catch (progressError) { setError(progressError.message); }
    };
    const queueInstructions = async () => {
        setBusy('instructions'); setInstructionNotice('');
        try {
			const response = await sendInstructions(selectedId);
			setInstructionNotice(`Queued for ${response.recipient_email}. Waiting for the mail service to accept it…`);
			const delivery = await waitForInstructionDelivery(selectedId, response.delivery_id);
			if (delivery?.status === 'sent') {
				setInstructionNotice(`Accepted by the mail service for ${delivery.recipient_email}. Mailbox delivery is not yet confirmed.`);
			} else if (delivery?.status === 'failed') {
				setError(`The instruction email was not accepted: ${delivery.failure_reason || 'the mail service returned an error.'}`);
			} else {
				setInstructionNotice(`Still queued for ${response.recipient_email}. The mail worker has not confirmed acceptance yet.`);
			}
		}
        catch (sendError) { setError(sendError.message); }
        finally { setBusy(''); }
    };
    const changeBindingState = async (nextState, reason, confirmation) => {
        if (confirmation && !window.confirm(confirmation)) return;
        setBusy(`state-${nextState}`); setError(''); setActionNotice('');
        try { await transitionManagedBinding(selectedId, nextState, reason); await load(); }
        catch (transitionError) { setError(transitionError.message); }
        finally { setBusy(''); }
    };
    const stop = async () => {
        setBusy('stop'); setError(''); setActionNotice('');
        try {
            const result = await stopManagedBinding(selectedId);
            const count = Number(result?.cancelled_messages || 0);
            await load();
            setActionNotice(count
                ? `Stopped. ${count} queued ${count === 1 ? 'message was' : 'messages were'} cancelled before dispatch.`
                : 'Stopped. No queued messages were waiting for dispatch.');
        } catch (stopError) { setError(stopError.message); }
        finally { setBusy(''); }
    };
    const credentialField = [...(definition.accountFields || []).map((item) => ({ ...item, scope: 'account' })), ...(visibleDefinition.bindingFields || []).map((item) => ({ ...item, scope: 'binding' }))].find((item) => item.secret);
    const showCredentialReplacement = () => {
        if (!credentialField) return;
        document.getElementById(`binding-field-${credentialField.scope}-${credentialField.name}`)?.focus();
		setActionNotice(`Enter the replacement ${credentialField.label.toLowerCase()} above. It remains a draft until Test passes.`);
    };

    const passed = readiness?.state === 'passed';
    const bindingState = selected?.binding?.state || '';
	const accountDisplayValues = channelId === 'messenger'
		? { ...accountDraft, ...accountSecrets, ...messengerDisplayValues(bindingDraft.page_id) }
		: { ...accountDraft, ...accountSecrets };

    useEffect(() => {
        onControlStateChange?.({
            hasTarget: Boolean(selected),
            state: bindingState,
            busy: Boolean(busy),
            editing: Boolean(editing),
			playLabel: bindingState === 'paused' ? 'Resume' : 'Test',
			canPlay: Boolean(selected) && !unavailable && !busy && canAdmin,
            canPause: Boolean(selected) && !busy && bindingState === 'active',
            canStop: Boolean(selected) && !busy && ['active', 'paused'].includes(bindingState),
            canDisable: Boolean(selected) && !busy && bindingState !== 'disabled',
            canEdit: !busy && canAdmin && !unavailable && bindingState !== 'disabled',
            readinessPassed: passed,
        });
    }, [bindingState, busy, canAdmin, editing, onControlStateChange, passed, selected, unavailable]);

	// Rescue pending edits before an idle sign-out.
	//
	// This panel already writes its edits to the server 700ms after a keystroke,
	// so its work is almost never unsaved. "Almost" is the problem: a person who
	// types and immediately walks away leaves that last edit sitting in a
	// timer. Flushing it at the 28-minute warning closes the gap.
	//
	// There is no local draft here because there is nowhere better for the work
	// to go. The server already holds every earlier keystroke.
	useDraftSaver(async () => {
		if (!dirtyRef.current.account && !dirtyRef.current.binding) return undefined;
		try {
			await flushDraft();
			return { saved: true, where: 'cloud', label: definition.label || definition.bindingName };
		} catch (err) {
			return { saved: false, where: 'cloud', error: err.message };
		}
	}, [flushDraft, definition.label, definition.bindingName]);

    useImperativeHandle(controlRef, () => ({
		flushDraft,
        play: () => bindingState === 'paused'
            ? changeBindingState('active', 'Resumed from the Bindings manager')
            : runTest(),
        pause: () => changeBindingState('paused', 'Paused from the Bindings manager'),
        stop,
        disable: () => changeBindingState(
            'disabled',
            'Disabled from the Bindings manager',
            'Disable this binding permanently? A disabled binding cannot be reopened; create a new binding instead.',
        ),
        edit: () => {
            onEditingChange?.(true);
            window.setTimeout(() => document.querySelector('.bindings-manager__scroll input:not([type="radio"]), .bindings-manager__scroll select, .bindings-manager__scroll textarea')?.focus(), 0);
        },
	}));

    const testSection = <>
        <section className="binding-test-contract"><HelpCircle size={18} /><div><b>{isGoogleChat && isSender ? 'Check this sender' : 'Test this configuration'}</b><p>{visibleDefinition.testNote}</p></div><Button variant="secondary" disabled={!canAdmin || !selectedId || unavailable || Boolean(busy)} onClick={runTest}>{busy === 'test' ? 'Testing…' : (visibleDefinition.testLabel || 'Test')}</Button></section>
        {readiness && <section className={`binding-readiness is-${readiness.state}`}><b>{readiness.summary}</b>{readiness.issues?.length > 0 && <><p>Problems found:</p><ul>{readiness.issues.map((item) => <li key={item}>{item}</li>)}</ul></>}</section>}
    </>;
    const draftValues = {
        ...bindingDraft,
        ...bindingSecrets,
        ...(isGoogleChat ? { authorization_mode: isSender ? 'Domain-wide delegated Workspace user' : 'Google-signed request' } : {}),
        ...(isGoogleChat && !isSender ? googleChatDisplayValues(bindingDraft.project_number) : {}),
    };
    const googleFieldsDisabled = !canAdmin || unavailable || !selected || bindingState === 'disabled' || Boolean(busy);
    const googleEdit = (item, value) => { onEditingChange?.(true); edit('binding', item, value); };
    const selectList = (items) => <div className="binding-selector__list">{items.map((item) => <button type="button" disabled={Boolean(busy)} className={item.binding.binding_id === selectedId ? 'is-active' : ''} key={item.binding.binding_id} onClick={() => selectBinding(item.binding.binding_id)}><span>{item.binding.display_label}</span><small>{item.binding.state.replaceAll('_', ' ')}</small></button>)}</div>;

    return (
        <div className={`channel-binding-panel${unavailable ? ' is-disabled' : ''}`}>
            {!isGoogleChat && <section className="binding-overview">
                <div><h3>{visibleDefinition.title}</h3><p>{visibleDefinition.summary}</p></div>
                {unavailable && <span className="manager-status manager-status--warning"><AlertTriangle size={14} /> Provider access unavailable</span>}
            </section>}

            {isGoogleChat ? <>
                <Context title="Your sender (DCM)" defaultOpen>
                    <div className="binding-section-heading">
                        <p>The Workspace user your surveys are sent as.</p>
                        <Button variant="secondary" disabled={!canAdmin || unavailable || Boolean(busy)} onClick={() => add('workspace_user')}><Plus size={15} /> Add sender</Button>
                    </div>
                    {selectList((state?.bindings || []).filter(isWorkspaceSender))}
                    {selected && isSender ? <>
                        {(visibleDefinition.dcmGroups || []).map((group) => <Context key={group.title} title={group.title} defaultOpen sub>
                            <GoogleFieldGrid names={group.names} definition={visibleDefinition} values={draftValues} secrets={secretPresence} disabled={googleFieldsDisabled} onChange={googleEdit} />
                        </Context>)}
                        <p className="binding-status">Status: {bindingState.replaceAll('_', ' ')}</p>
                        {testSection}
                    </> : <p>Add or select a sender.</p>}
                </Context>
                <Context title="Recipient-requested (CRM)">
                    <p>When a recipient chooses Google Chat from an emailed invitation, your sender above delivers the survey. There is nothing separate to set up.</p>
                    {(state?.bindings || []).some((item) => !isWorkspaceSender(item)) && <>
                        <p>The application connection below served the earlier recipient-started flow. It stays manageable here until that flow's replacement is accepted.</p>
                        {selectList((state?.bindings || []).filter((item) => !isWorkspaceSender(item)))}
                        {selected && !isSender && <>
                            {(visibleDefinition.dcmGroups || []).map((group) => <Context key={group.title} title={group.title} sub>
                                <GoogleFieldGrid names={group.names} definition={visibleDefinition} values={draftValues} secrets={secretPresence} disabled={googleFieldsDisabled} onChange={googleEdit} />
                            </Context>)}
                            <p className="binding-status">Status: {bindingState.replaceAll('_', ' ')}</p>
                            <MemberTable definition={definition} members={selected?.members || []} />
                            {testSection}
                        </>}
                    </>}
                </Context>
                <InstructionPanel definition={visibleDefinition} bindingId={selectedId} progress={parse(selected?.settings?.instruction_progress)} onProgress={updateProgress} onSend={queueInstructions} sending={busy === 'instructions'} />
            </> : <>
                <FieldSet scope="account" title="Account settings" fields={definition.accountFields} values={accountDisplayValues} secrets={secretPresence} disabled={!canAdmin || unavailable || !editing || Boolean(busy)} onChange={(item, value) => edit('account', item, value)} />
                <section className="binding-selector">
                    <div className="binding-section-heading"><div><h3>{definition.bindingName[0].toUpperCase() + definition.bindingName.slice(1)} bindings</h3><p>Select the organization-owned identity to configure.</p></div><Button variant="secondary" disabled={!canAdmin || unavailable || Boolean(busy)} onClick={() => add()}><Plus size={15} /> {definition.addLabel}</Button></div>
                    {selectList(state?.bindings || [])}
                    {!state?.bindings?.length && <p>No {definition.bindingName} has been added.</p>}
                </section>
                {selected && <section className="binding-health"><span><b>Status</b> {selected.binding.state.replaceAll('_', ' ')}</span><span><b>Connection</b> {channelId === 'whatsapp' || channelId === 'sms' || channelId === 'rcs' ? 'SamSurveyor-managed transport' : secretPresence.size ? 'Credential stored' : 'Not configured'}</span><span><b>Inbound</b> {selected.binding.last_inbound_at || '—'}</span><span><b>Do not contact</b> —</span></section>}
                {credentialField && editing && <button type="button" className="binding-replace-link" onClick={showCredentialReplacement}>Replace credential</button>}
                <FieldSet scope="binding" title={`${definition.bindingName[0].toUpperCase() + definition.bindingName.slice(1)} settings`} fields={visibleDefinition.bindingFields} values={{ ...bindingDraft, ...bindingSecrets }} secrets={secretPresence} disabled={!editing || !canAdmin || unavailable || !selected || Boolean(busy)} onChange={(item, value) => edit('binding', item, value)} />
                <MemberTable definition={definition} members={selected?.members || []} />
                <InstructionPanel definition={visibleDefinition} bindingId={selectedId} progress={parse(selected?.settings?.instruction_progress)} onProgress={updateProgress} onSend={queueInstructions} sending={busy === 'instructions'} />
                {testSection}
            </>}
            {actionNotice && <div className="binding-notice"><RefreshCw size={15} /> {actionNotice}</div>}
            {instructionNotice && <div className="binding-notice"><Send size={15} /> {instructionNotice}</div>}
            {error && <div className="binding-error" role="alert">{error}</div>}
            {editing && <footer className="binding-action-bar"><p>Save a partial draft at any time. Test verifies the saved connection and activates it only when every check passes.</p><div><Button variant="secondary" onClick={onCancel}>Cancel</Button><Button disabled={!canAdmin || !state || unavailable || Boolean(busy) || (!selectedId && !definition.accountFields?.length)} onClick={save}><Check size={15} /> {busy === 'save' ? 'Saving…' : 'Save draft'}</Button></div></footer>}
        </div>
    );
});

export default ChannelBindingPanel;
